import type { ReactNode } from 'react';
import type { GroupBase } from 'react-select';

import Select from '../Select/Select';
import type { SelectProps } from '../Select/Select';

import FormLabel from './FormLabel';
import type { FormLabelVariant } from './FormLabel';

type Props<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectProps<Option, IsMulti, Group> & {
  /**
   * Text (or JSX nodes) to be rendered as the label.
   */
  label: ReactNode;
  /**
   * If the label will appear inline (instead of the default block styling).
   */
  isInline?: boolean;
  /**
   * Which type of form label to use.
   */
  variant?: FormLabelVariant;
  /**
   * Whether or not this field is required. If true, the label will display "(Required)" next to it.
   */
  isRequired?: boolean;
};

const LabeledSelect = <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
  { label, isInline, variant, isRequired, ...props }: Props<Option, IsMulti, Group>,
) => (
  <FormLabel
    label={label}
    variant={variant}
    isInline={isInline}
    htmlFor={`${props.id}-input`}
    requiredText={isRequired ? '(Required)' : undefined}
  >
    <Select
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      inputId={`${props.id}-input`}
    />
  </FormLabel>);

export default LabeledSelect;
